import { LevelCatalog } from "@highmarch/app";
import type { BalanceData, PlayerProfile } from "@highmarch/domain";
import { icon } from "../iconsKit";
import { btnHtml, fmtCompact } from "../kit";
import type { LootFlight } from "../loot";
import { portraitUrl, uiUrl } from "../portraits";
import { paintedIconHtml, paintedIconUrl } from "../featureIcons";
import { STOREFRONT_ICON } from "../shops/shopsArt";
import type { HubTab } from "./sheetGuard";
import type { BuilderPick } from "./builderPick";
import { Badge, badgeHtml, chestsBadge, gearBadge, towersBadge } from "./badges";
import type { Readiness } from "./readiness";
import { battleCardHtml } from "./battleCard";
import type { HubFeatures } from "./hubFeatures";
import { mergeBadge } from "../../features/registry";
import { upgradeNudge } from "./upgradeNudge";

/**
 * The hub's markup (hubView.ts renders it): the top bar (commander, the features' pills — since the shop redesign of
 * 2026-10-19 only gems, as the Store button, and the VIP chip: every other currency shows on the screen that spends it —
 * the ⚙ menu button), the
 * chip slots, the battle card and PLAY, and the tab bar with its badges — M35 Option 1: Shop · Towers · BATTLE ·
 * Armoury · Events, each tab a painted icon over its name, BATTLE a raised gold plate naming the next level. Strings
 * only; the view binds the handlers.
 */

/** What one render of the hub shows. */
export interface HubMarkupIn {
  readonly catalog: LevelCatalog;
  readonly profile: PlayerProfile;
  readonly features: HubFeatures;
  readonly next: number;
  readonly readiness: Readiness;
  readonly pick: BuilderPick;
  /** Chest loot still in the air: the pills show this much less until it lands. */
  readonly inFlight: LootFlight | null;
  /** The Shop, the Armoury and Events are still locked. */
  readonly locked: boolean;
  /** The lit tab. */
  readonly tab: HubTab;
  /** The Towers tab shows ✓ (a job finished while the hub was open). */
  readonly towersDone: boolean;
  /** Owned towers not yet seen on the Towers page. */
  readonly newTowers: number;
  /** Gear won since the Armoury last opened (the Armoury tab's NEW). */
  readonly newGear?: number;
  /** The Shop tab's own red count (research to buy; the features add theirs). */
  readonly shopDots?: number;
  readonly now: number;
}

/** The tab bar's painted icons (M35 mock-up: the merchant, an archer tower, an epic ammo pouch, the event ember). */
export function tabIconUrl(tab: Exclude<HubTab, "battle">): string {
  switch (tab) {
    case "shop": return uiUrl(STOREFRONT_ICON);
    case "towers": return portraitUrl("archer", 2) ?? "";
    case "gear": return uiUrl("gear/gear_ammo_epic.png");
    case "events": return paintedIconUrl("eventEmber");
  }
}

export function hubHtml(m: HubMarkupIn): string {
  const p = m.profile, next = m.next, locked = m.locked;
  const tab = (id: Exclude<HubTab, "battle">, label: string, badge = "", lock = false) => tabHtml(id, m.tab, `<img class="tic" decoding="sync" src="${tabIconUrl(id)}" alt="">`, label, badge, lock);
  const allCleared = m.catalog.highestLevelCleared >= m.catalog.levelCount;
  return `
      <div class="topbar">
        <button class="plate commander" aria-label="Commander, power ${p.powerScore}"><div class="face emblem">${icon("flag", 20, "#fff8e6")}</div><div class="text"><b class="disp gold">Commander</b><small><span class="pw">${fmtCompact(p.powerScore)}</span></small></div></button>
        <button type="button" class="menubtn" aria-label="Menu: settings, guide, achievements and more">${paintedIconHtml("settings", 26)}<span class="mdot">${menuDotHtml(m.features)}</span></button>
        <div class="pills">${m.features.pillsHtml()}</div>
      </div>
      <div class="chips"><button class="bchip hidden"></button><div class="ghostchip hidden"></div></div>
      <div class="dock">
        ${battleCardHtml(m.catalog, next, m.readiness, m.now)}
        ${btnHtml("PLAY", "gold", { icon: "play", className: "play accent node next" })}
      </div>
      <div class="tabbar">
        ${tab("shop", "Shop", `<span class="sbadge">${shopBadgeHtml(m.features, m.shopDots ?? 0, locked)}</span>`, locked)}
        ${tab("towers", "Towers", `<span class="tbadge">${towersBadgeHtml(m.features, m.pick, m.towersDone, m.newTowers)}</span>`)}
        <button class="tab battle${m.tab === "battle" ? " on" : ""}" data-tab="battle" aria-label="Battle: ${allCleared ? "the campaign map" : `level ${next}`}"><span class="bplate disp">BATTLE</span><small class="blevel">${allCleared ? "Campaign" : `Level ${next}`}</small></button>
        ${tab("gear", "Armoury", badgeHtml(m.features.badge("gear", gearBadge(m.newGear ?? 0, !locked))), locked)}
        ${tab("events", "Events", `<span class="ebadge">${eventsBadgeHtml(m.catalog, m.features, m.now, locked)}</span>`, locked)}
      </div>`;
}

export function tabHtml(tab: HubTab, current: HubTab, iconHtml: string, label: string, badge = "", locked = false): string {
  return `<button class="tab${tab === current ? " on" : ""}${locked ? " locked-tab" : ""}" data-tab="${tab}">${iconHtml}<span>${label}</span>${locked ? `<i class="lk">${icon("lock", 11)}</i>` : badge}</button>`;
}

/**
 * Events (M35; the Chests tab's Daily Orders moved here): red for orders, Orders chests or a check-in to claim plus the
 * red dots of the rows it lists (an event's rewards or a new event, Endless's chest, free rewards); else, while the
 * day's orders are under way, a blue "done/total" (e.g. 1/3).
 */
export function eventsBadgeHtml(catalog: LevelCatalog, features: HubFeatures, now: number, locked: boolean): string {
  const o = catalog.orders, B: BalanceData = catalog.balance;
  const claimable = o.claimable() + (o.unlocked(catalog.highestLevelCleared) && catalog.checkin.canClaim(now) ? 1 : 0);
  const open = o.orders.some((x) => !x.claimed);
  const core = chestsBadge(claimable, !locked, open ? { done: o.supplyProgress(now), total: B.orders.ordersPerDay } : null);
  return locked ? "" : badgeHtml(features.badge("events", mergeBadge(core, features.moreDots("events"))));
}

/** Shop: a red count of what is waiting in it — research a player can buy, the shops' featured slots, VIP's free chest. */
export function shopBadgeHtml(features: HubFeatures, own: number, locked: boolean): string {
  if (locked) return "";
  const core: Badge | null = own > 0 ? { tone: "alert", text: String(own) } : null;
  return badgeHtml(features.badge("shop", core));
}

export function towersBadgeHtml(features: HubFeatures, pick: BuilderPick, done: boolean, newTowers: number): string {
  return badgeHtml(features.badge("towers", towersBadge(pick, done, newTowers, !upgradeNudge.shows(pick))));
}

/** The ⚙ button's dot: the red counts of the menu's rows (an achievement to claim). */
export function menuDotHtml(features: HubFeatures): string {
  const n = features.moreDots("menu");
  return n > 0 ? `<span class="badge alert">${n}</span>` : "";
}
